
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface Profile {
  id: string;
  username: string;
  first_name?: string;
  last_name?: string;
  birthdate?: string;
  profile_picture?: string;
  relationship_type?: string;
  referral_code?: string;
}

interface ProfileSwitchContextType {
  activeProfile: Profile | null;
  ownProfile: Profile | null;
  additionalProfiles: Profile[];
  isLoadingProfiles: boolean;
  switchToProfile: (profileId: string) => void;
  refreshProfiles: () => Promise<void>;
  createAdditionalProfile: (profileData: Omit<Profile, 'id'>) => Promise<string>;
}

const ProfileSwitchContext = createContext<ProfileSwitchContextType | undefined>(undefined);

export const ProfileSwitchProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [activeProfile, setActiveProfile] = useState<Profile | null>(null);
  const [ownProfile, setOwnProfile] = useState<Profile | null>(null);
  const [additionalProfiles, setAdditionalProfiles] = useState<Profile[]>([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);

  // Load user's own profile and additional profiles
  const loadProfiles = async () => {
    if (!user?.id) return;
    
    setIsLoadingProfiles(true);
    
    try {
      console.log('🔍 ProfileSwitchContext - Loading profiles for user:', user.id);
      
      // Load own profile
      const { data: ownProfileData, error: ownError } = await supabase
        .from('profiles')
        .select('id, username, first_name, last_name, birthdate, profile_picture, referral_code')
        .eq('id', user.id)
        .single();

      console.log('🔍 ProfileSwitchContext - Own profile data:', ownProfileData);
      console.log('🔍 ProfileSwitchContext - Own profile referral_code:', ownProfileData?.referral_code);

      if (ownError) {
        console.error('Error loading own profile:', ownError);
      } else if (ownProfileData) {
        const ownProfileFormatted = {
          ...ownProfileData,
          relationship_type: 'own' // Mark as own profile
        };
        setOwnProfile(ownProfileFormatted);
        
        // Set as active profile if no active profile is set or if active profile is the user's own
        if (!activeProfile || activeProfile.id === user.id) {
          setActiveProfile(ownProfileFormatted);
        }
      }

      // Load additional profiles using the RPC function
      const { data: additionalProfilesData, error: additionalError } = await supabase
        .rpc('get_owned_profiles', { owner_user_id: user.id });

      console.log('🔍 ProfileSwitchContext - Additional profiles raw data:', additionalProfilesData);

      if (additionalError) {
        console.error('Error loading additional profiles:', additionalError);
      } else {
        const profiles = (additionalProfilesData || []).map((profile: any) => {
          console.log('🔍 ProfileSwitchContext - Processing profile:', profile.username, 'referral_code:', profile.referral_code);
          return {
            id: profile.profile_id,
            username: profile.username,
            first_name: profile.first_name,
            last_name: profile.last_name,
            birthdate: profile.birthdate,
            relationship_type: profile.relationship_type,
            referral_code: profile.referral_code
          };
        });
        
        console.log('🔍 ProfileSwitchContext - Processed additional profiles:', profiles);
        profiles.forEach(p => {
          console.log(`🔍 Profile ${p.username}: referral_code = ${p.referral_code}`);
        });
        
        setAdditionalProfiles(profiles);
        
        console.log('🔄 ProfileSwitchContext - Loaded profiles:', {
          ownProfile: ownProfileData ? ownProfileData.username : null,
          additionalProfiles: profiles.map(p => p.username),
          currentActiveProfile: activeProfile?.username
        });
      }
    } catch (error) {
      console.error('Error in loadProfiles:', error);
    } finally {
      setIsLoadingProfiles(false);
    }
  };

  // Switch to a different profile
  const switchToProfile = (profileId: string) => {
    console.log('🔄 ProfileSwitchContext - Switching to profile:', profileId);
    
    // Check if switching to own profile
    if (ownProfile && ownProfile.id === profileId) {
      console.log('🔄 ProfileSwitchContext - Switching to own profile:', ownProfile.username);
      setActiveProfile(ownProfile);
      return;
    }
    
    // Check if switching to a managed profile
    const targetProfile = additionalProfiles.find(p => p.id === profileId);
    if (targetProfile) {
      console.log('🔄 ProfileSwitchContext - Switching to managed profile:', targetProfile.username);
      setActiveProfile(targetProfile);
      return;
    }
    
    console.warn('🔄 ProfileSwitchContext - Profile not found:', profileId);
  };

  // Create additional profile
  const createAdditionalProfile = async (profileData: Omit<Profile, 'id'>): Promise<string> => {
    if (!user?.id) throw new Error('User not authenticated');

    console.log('🔍 ProfileSwitchContext - Creating additional profile:', profileData);

    const { data, error } = await supabase
      .rpc('create_additional_profile', {
        owner_user_id: user.id,
        profile_data: {
          username: profileData.username,
          firstName: profileData.first_name,
          lastName: profileData.last_name,
          birthdate: profileData.birthdate
        },
        relationship_type: profileData.relationship_type || 'managed'
      });

    if (error) {
      console.error('Error creating additional profile:', error);
      throw error;
    }

    console.log('🔍 ProfileSwitchContext - Created profile with ID:', data);

    // Refresh profiles to include the new one
    await loadProfiles();
    
    return data;
  };

  // Load profiles when user changes
  useEffect(() => {
    if (user?.id) {
      loadProfiles();
    } else {
      setActiveProfile(null);
      setOwnProfile(null);
      setAdditionalProfiles([]);
    }
  }, [user?.id]);

  // Reset active profile to own profile when user changes
  useEffect(() => {
    if (ownProfile && (!activeProfile || activeProfile.id !== ownProfile.id)) {
      // Only auto-switch to own profile if we don't have an active profile yet
      if (!activeProfile) {
        setActiveProfile(ownProfile);
      }
    }
  }, [ownProfile]);

  const value = {
    activeProfile,
    ownProfile,
    additionalProfiles,
    isLoadingProfiles,
    switchToProfile,
    refreshProfiles: loadProfiles,
    createAdditionalProfile
  };

  return (
    <ProfileSwitchContext.Provider value={value}>
      {children}
    </ProfileSwitchContext.Provider>
  );
};

export const useProfileSwitch = () => {
  const context = useContext(ProfileSwitchContext);
  if (context === undefined) {
    throw new Error('useProfileSwitch must be used within a ProfileSwitchProvider');
  }
  return context;
};
